import React from 'react';
import type { ISODateRange } from 'shared/src/date-string-utils/types';
import type { UnitPriceType } from 'shared/src/domain/line-item/line-item-channels';
import { getUnitName } from 'shared/src/domain/line-item/line-item-utils';
import { useCubeQuery } from '@/components/display/use-cube-query';
import type { TimeFrameMode } from './time-frame-utils';

type PlannedValuesProps = {
  lineItemId: string;
  dateRange: ISODateRange;
  category: TimeFrameMode;
};

export function PlannedValues({ lineItemId, dateRange, category }: PlannedValuesProps) {
  const showPerDay = category === 'lifetime' || category === 'pacing_blocks';

  const result = useCubeQuery({
    measures: [
      'line_item_metrics_daily.planned_spend',
      'line_item_metrics_daily.planned_units',
      'line_item_metrics_daily.planned_daily_spend',
      'line_item_metrics_daily.planned_daily_units'
    ],
    dimensions: ['line_items.unit_price_type_name'],
    timeDimensions: [
      {
        dimension: 'line_item_metrics_daily.day',
        dateRange: [dateRange.from, dateRange.to]
      }
    ],
    filters: [
      {
        member: 'line_item_metrics_daily.line_item_id',
        operator: 'equals',
        values: [lineItemId]
      }
    ]
  });

  if (result.status === 'loading') return <PlannedValuesSkeleton />;
  if (result.status === 'error') return null;

  const row = result.formattedRows[0];
  const rawRow = result.rows[0];
  if (!row || !rawRow) return null;

  const unitAbbr = getUnitAbbr(rawRow['line_items.unit_price_type_name']);

  return (
    <div className="flex shrink-0 items-center gap-2">
      <span className="text-xs font-medium text-gray-800">Planned</span>
      <div className="flex flex-wrap items-center justify-end gap-1">
        <ValuePill value={row['line_item_metrics_daily.planned_spend']} unit="budget" />
        {showPerDay ? (
          <ValuePill value={row['line_item_metrics_daily.planned_daily_spend']} unit="/day" />
        ) : null}
        <ValuePill value={row['line_item_metrics_daily.planned_units']} unit={unitAbbr} />
        {showPerDay ? (
          <ValuePill
            value={row['line_item_metrics_daily.planned_daily_units']}
            unit={`${unitAbbr}/day`}
          />
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function PlannedValuesSkeleton() {
  return (
    <div className="flex shrink-0 items-center gap-2">
      <span className="text-xs font-medium text-gray-800">Planned</span>
      <div className="flex items-center gap-1">
        <div className="h-5 w-20 animate-pulse rounded bg-gray-200" />
        <div className="h-5 w-20 animate-pulse rounded bg-gray-200" />
      </div>
    </div>
  );
}

function ValuePill({ value, unit }: { value: string; unit: string }) {
  return (
    <span className="flex h-5 items-center rounded bg-slate-50 px-1">
      <span className="text-xs font-medium text-gray-800">{value}</span>
      <span className="pl-1 text-[11px] text-gray-600">{unit}</span>
    </span>
  );
}

function getUnitAbbr(unitPriceTypeName: unknown): string {
  const name = unitPriceTypeName as UnitPriceType['name'];
  const { abbr } = getUnitName(name);
  return abbr || 'units';
}
